/**
 * The qurbani season screen (§12.1 item 4).
 *
 * Eight weeks a year this is the most important screen in the app, and it
 * leads with the two things a partner can still act on: how long is left, and
 * which animals will not make their target weight. An animal 30 kg short four
 * weeks out can be fed more; the same animal on Eid morning cannot.
 *
 * The Eid date is typed in rather than stored. It moves with the lunar
 * calendar, so a value saved last year would quietly give the wrong countdown.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { formatMoney } from '../../lib/currency.js';
import { useQurbaniSeason, type SeasonAnimal } from './api.js';

function AnimalRow({ animal }: { animal: SeasonAnimal }): JSX.Element {
  const { t } = useTranslation();

  return (
    <li
      className={`rounded-xl bg-white px-4 py-3 shadow-sm ${animal.willMissTarget ? 'border-l-4 border-amber-500' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900">{animal.tag}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {t('qurbani.nowWeight', { kg: animal.currentWeightKg })}
            {animal.projectedWeightKg !== null && ` · ${t('qurbani.projected', { kg: animal.projectedWeightKg })}`}
          </p>
        </div>

        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
            animal.bookingStatus === 'booked'
              ? 'bg-green-50 text-green-800'
              : animal.bookingStatus === 'delivered'
                ? 'bg-gray-100 text-gray-600'
                : 'bg-amber-50 text-amber-800'
          }`}
        >
          {animal.bookingStatus === null ? t('qurbani.unbooked') : t(`qurbani.status.${animal.bookingStatus}`)}
        </span>
      </div>

      {animal.willMissTarget && (
        <p className="mt-1 text-xs font-medium text-amber-700">
          {t('qurbani.shortBy', { kg: animal.shortfallKg ?? '0' })}
        </p>
      )}

      {animal.dailyGainKg === null && <p className="mt-1 text-xs text-gray-400">{t('qurbani.needsWeighing')}</p>}

      {animal.customerName !== null && (
        <p className="mt-1 text-xs text-gray-500">{t('qurbani.bookedBy', { name: animal.customerName })}</p>
      )}
    </li>
  );
}

export function QurbaniSeason(): JSX.Element {
  const { t } = useTranslation();
  const [eidDate, setEidDate] = useState('');
  const { data, isLoading, isError, refetch } = useQurbaniSeason(eidDate);

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('qurbani.title')}</h1>

      <label className="block text-xs text-gray-500" htmlFor="eid-date">
        {t('qurbani.eidDate')}
      </label>
      <input
        id="eid-date"
        type="date"
        value={eidDate}
        onChange={(event) => setEidDate(event.target.value)}
        className="mb-4 mt-1 min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />

      {eidDate === '' && <p className="py-8 text-center text-sm text-gray-500">{t('qurbani.pickDate')}</p>}

      {isLoading && eidDate !== '' && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {data !== undefined && (
        <>
          {/* The countdown, first, because everything else is judged against it. */}
          <div className={`mb-4 rounded-xl px-4 py-4 text-white ${data.isOver ? 'bg-gray-500' : 'bg-green-700'}`}>
            <p className="text-xs opacity-90">{t('qurbani.countdown')}</p>
            <p className="mt-0.5 text-3xl font-semibold tabular-nums">
              {data.isOver ? t('qurbani.over') : t('qurbani.daysLeft', { days: data.daysRemaining })}
            </p>
          </div>

          {data.atRiskCount > 0 && (
            <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t('qurbani.atRisk', { count: data.atRiskCount })}
            </p>
          )}

          <section className="mb-4 grid grid-cols-2 gap-2" aria-label={t('qurbani.seasonPnl')}>
            <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
              <p className="text-xs text-gray-500">{t('qurbani.sold')}</p>
              <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{data.pnl.animalsSold}</p>
            </div>
            <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
              <p className="text-xs text-gray-500">{t('qurbani.profit')}</p>
              <p
                className={`mt-0.5 text-lg font-semibold tabular-nums ${
                  data.pnl.profit.startsWith('-') ? 'text-red-700' : 'text-gray-900'
                }`}
              >
                {formatMoney(data.pnl.profit, { decimals: 'never' })}
              </p>
            </div>
          </section>

          {data.pnl.marginPercent !== null && (
            <p className="mb-4 text-xs text-gray-500">{t('qurbani.margin', { percent: data.pnl.marginPercent })}</p>
          )}

          <ul className="space-y-2">
            {data.animals.map((animal) => (
              <AnimalRow key={animal.animalId} animal={animal} />
            ))}
          </ul>

          {data.animals.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500">{t('qurbani.noTargets')}</p>
          )}
        </>
      )}
    </div>
  );
}
